/**
 * Mission History Utilities
 * Helper functions for mission history calculations and formatting
 */

/**
 * Calculate mission statistics from history
 * @param {Array} missions - Array of mission history entries
 * @param {boolean} isMetric - Whether to use metric units
 * @returns {Object} Statistics object
 */
export const calculateMissionStatistics = (missions, isMetric = true) => {
  if (!missions || missions.length === 0) {
    return {
      totalMissions: 0,
      totalDistance: 0,
      averageDistance: 0,
      lastMission: null
    };
  }

  const totalMissions = missions.length;
  const totalDistanceKm = missions.reduce((sum, mission) => sum + (mission.distance || 0), 0);
  const averageDistanceKm = totalDistanceKm / totalMissions;

  // Convert to appropriate unit
  const convertDistance = (km) => {
    if (isMetric) return km;
    return km * 0.621371; // Convert to miles
  };

  const distanceUnit = isMetric ? 'km' : 'mi';

  // Get last mission (most recent)
  const sortedMissions = [...missions].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );
  const lastMission = sortedMissions[0];

  return {
    totalMissions,
    totalDistance: convertDistance(totalDistanceKm).toFixed(1),
    averageDistance: convertDistance(averageDistanceKm).toFixed(1),
    distanceUnit,
    lastMission: lastMission ? {
      name: lastMission.missionName,
      date: new Date(lastMission.timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      route: `${lastMission.route?.start || 'Unknown'} → ${lastMission.route?.destination || 'Unknown'}`
    } : null
  };
};

/**
 * Prepare health trend data for chart
 * @param {Array} missions - Array of mission history entries
 * @returns {Array} Chart data points
 * @deprecated - Health trend chart removed, keeping function for potential future use
 */
export const prepareHealthTrendData = (missions) => {
  if (!missions || missions.length === 0) {
    return [];
  }

  // Sort missions by date (oldest first for chart)
  const sortedMissions = [...missions].sort((a, b) => 
    new Date(a.timestamp) - new Date(b.timestamp)
  );

  // Create data points for chart
  return sortedMissions.map((mission, index) => ({
    name: mission.missionName,
    date: new Date(mission.timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    healthBefore: mission.healthChange?.before?.overall || 0,
    healthAfter: mission.healthChange?.after?.overall || 0,
    missionIndex: index + 1
  }));
};

/**
 * Format mission duration
 * @param {string} duration - Duration string (e.g., "2h 30m")
 * @returns {string} Formatted duration
 */
export const formatMissionDuration = (duration) => {
  if (!duration) return 'N/A';
  return duration;
};

/**
 * Get status badge color classes
 * @param {string} status - Mission status
 * @returns {Object} Color classes for badge
 */
export const getMissionStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return {
        bg: 'bg-green-500/20',
        text: 'text-green-400',
        border: 'border-green-500/30',
        icon: '✅'
      };
    case 'in-progress':
      return {
        bg: 'bg-blue-500/20',
        text: 'text-blue-400',
        border: 'border-blue-500/30',
        icon: '🔵'
      };
    case 'cancelled':
      return {
        bg: 'bg-red-500/20',
        text: 'text-red-400',
        border: 'border-red-500/30',
        icon: '❌'
      };
    default:
      return {
        bg: 'bg-gray-500/20',
        text: 'text-gray-400',
        border: 'border-gray-500/30',
        icon: '⚪'
      };
  }
};

/**
 * Get health color based on percentage
 * @param {number} health - Health percentage
 * @returns {string} Color class
 */
export const getHealthColor = (health) => {
  if (health >= 80) return 'text-green-400';
  if (health >= 60) return 'text-yellow-400';
  if (health >= 40) return 'text-orange-400';
  return 'text-red-400';
};

/**
 * Get component wear color based on percentage
 * @param {number} wear - Wear percentage
 * @returns {string} Color class
 */
export const getWearColor = (wear) => {
  if (wear <= 2) return 'text-green-400';
  if (wear <= 5) return 'text-yellow-400';
  return 'text-red-400';
};

/**
 * Convert distance based on unit system
 * @param {number} km - Distance in kilometers
 * @param {boolean} isMetric - Whether to use metric units
 * @returns {string} Formatted distance with unit
 */
export const convertDistance = (km, isMetric = true) => {
  if (isMetric) {
    return `${km.toFixed(1)} km`;
  }
  return `${(km * 0.621371).toFixed(1)} mi`;
};
